import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Send } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function MembershipPage() {
  const [formData, setFormData] = useState({
    name: "",
    country: "",
    date_of_birth: "",
    interest: "",
    urgent_need: "",
    contact_number: "",
    email: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/membership`, formData);
      setSubmitted(true);
      toast.success("Welcome to IndonesiaTourism! Your membership has been registered.");
      setFormData({
        name: "",
        country: "",
        date_of_birth: "",
        interest: "",
        urgent_need: "",
        contact_number: "",
        email: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    "Exclusive member-only deals and discounts",
    "Priority booking for popular tours",
    "Personalized travel recommendations",
    "Access to private events and experiences",
    "Dedicated concierge support",
    "Early access to new products and services"
  ];

  return (
    <div data-testid="membership-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[350px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/10804651/pexels-photo-10804651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Bali rice terraces"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E2954]/90 to-[#0E2954]/60" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style text-white/70 mb-4">
              Join Our Community
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl text-white max-w-2xl">
              Become a Member Today
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl mb-6">
                Member Benefits
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-[#666666] text-lg mb-8">
                Join our exclusive membership program and unlock a world of benefits 
                designed to enhance your Indonesian experience.
              </motion.p>
              <motion.div variants={fadeInUp} className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#1B4D3E] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#1A1A1A]">{benefit}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                className="mt-12 p-8 bg-[#0E2954] rounded-lg"
              >
                <h3 className="text-white text-xl mb-4">Have Questions?</h3>
                <p className="text-white/70 mb-4">
                  Our team is here to help you with any inquiries about our services, 
                  products, or business collaboration opportunities.
                </p>
                <div className="space-y-2">
                  <p className="text-white">
                    <span className="text-white/70">WhatsApp:</span> +62-851-2150-1670
                  </p>
                  <p className="text-white">
                    <span className="text-white/70">Email:</span>{" "}
                    <a href="mailto:Avnerkrissy@gmail.com" className="underline">Avnerkrissy@gmail.com</a>
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              {submitted ? (
                <div className="bg-[#1B4D3E] rounded-lg p-12 text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-[#1B4D3E]" />
                  </div>
                  <h3 className="text-2xl text-white mb-4">Thank You!</h3>
                  <p className="text-white/70 mb-6">
                    Your membership registration has been received. 
                    We'll be in touch with you shortly.
                  </p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="text-white underline"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form 
                  onSubmit={handleSubmit}
                  data-testid="membership-form"
                  className="bg-white border border-[#E5E5E5] rounded-lg p-8"
                >
                  <h3 className="text-2xl mb-6">Membership Registration</h3>
                  
                  <div className="space-y-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        data-testid="membership-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input rounded-md"
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        data-testid="membership-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input rounded-md"
                        placeholder="your@email.com"
                      />
                    </div>

                    {/* Country */}
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium mb-2">
                        Country of Origin *
                      </label>
                      <input
                        type="text"
                        id="country"
                        name="country"
                        data-testid="membership-country-input"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="form-input rounded-md"
                        placeholder="Your country"
                      />
                    </div>

                    {/* Date of Birth */}
                    <div>
                      <label htmlFor="date_of_birth" className="block text-sm font-medium mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        id="date_of_birth"
                        name="date_of_birth"
                        data-testid="membership-dob-input"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        required
                        className="form-input rounded-md"
                      />
                    </div>

                    {/* Contact Number */}
                    <div>
                      <label htmlFor="contact_number" className="block text-sm font-medium mb-2">
                        Contact Number *
                      </label>
                      <input
                        type="tel"
                        id="contact_number"
                        name="contact_number"
                        data-testid="membership-phone-input"
                        value={formData.contact_number}
                        onChange={handleChange}
                        required
                        className="form-input rounded-md"
                        placeholder="+1 234 567 8900"
                      />
                    </div>

                    {/* Interest */}
                    <div>
                      <label htmlFor="interest" className="block text-sm font-medium mb-2">
                        Interest *
                      </label>
                      <select
                        id="interest"
                        name="interest"
                        data-testid="membership-interest-select"
                        value={formData.interest}
                        onChange={handleChange}
                        required
                        className="form-select rounded-md"
                      >
                        <option value="">Select your interest</option>
                        <option value="leisure">Leisure Travel</option>
                        <option value="business">Business Opportunities</option>
                        <option value="both">Both Leisure & Business</option>
                      </select>
                    </div>

                    {/* Urgent Need */}
                    <div>
                      <label htmlFor="urgent_need" className="block text-sm font-medium mb-2">
                        Specific Urgent Need (Optional)
                      </label>
                      <textarea
                        id="urgent_need"
                        name="urgent_need"
                        data-testid="membership-urgent-need-textarea"
                        value={formData.urgent_need}
                        onChange={handleChange}
                        rows={4}
                        className="form-input rounded-md resize-none"
                        placeholder="Tell us about any specific requirements or urgent needs..."
                      />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      data-testid="membership-submit-btn"
                      disabled={isSubmitting}
                      className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        "Submitting..."
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Submit Registration
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
